import { sum } from "lodash";
import { makeAutoObservable } from "mobx";
import * as yup from "yup";

import {
  CALORIES_PER_CARBOHYDRATE_GRAM,
  CALORIES_PER_FAT_GRAM,
  CALORIES_PER_PROTEIN_GRAM,
} from "@/config/constants";
import FormState from "@/store/FormState";
import { isBlank } from "@/utils/any";
import { fraction } from "@/utils/number";
import { toNum } from "@/utils/string";

import MealPlanFormStore from "./MealPlanFormStore";

export default class Macronutrients {
  // To make working with html inputs easier, we'll mainly update these as
  // strings, and have a getter to return them as numbers for calculations.
  carbohydrates = "";
  proteins = "";
  fats = "";

  formState = new FormState(
    this,
    yup
      .object({
        carbohydrates: yup.number().presence().required().min(0).max(100),
        proteins: yup.number().presence().required().min(0).max(100),
        fats: yup.number().presence().required().min(0).max(100),
      })
      .test(
        "isNotAbove100",
        "Macronutrient distribution exceeded. It should have a total of 100%",
        (value) => {
          return (
            sum([
              value?.carbohydrates ?? 0,
              value?.fats ?? 0,
              value?.proteins ?? 0,
            ]) <= 100
          );
        }
      )
      .test(
        "isNotBelow100",
        "Macronutrient distribution below 100%. It should have a total of 100%",
        (value) => {
          const allPresent = ![
            value?.carbohydrates,
            value?.fats,
            value?.proteins,
          ].some(isBlank);
          return allPresent
            ? sum([
                value?.carbohydrates ?? 0,
                value?.fats ?? 0,
                value?.proteins ?? 0,
              ]) >= 100
            : true;
        }
      )
      .defined()
  );
  errorMessage?: string;

  private store: MealPlanFormStore;

  get carbohydratePercent(): number {
    return fraction(+this.carbohydrates);
  }

  get proteinPercent(): number {
    return fraction(+this.proteins);
  }
  get fatPercent(): number {
    return fraction(+this.fats);
  }

  get carbohydrateGrams(): number {
    return Macronutrients.macronutrientGrams(
      "carbohydrates",
      this.carbohydratePercent,
      this.store.recommendedCaloriesKcal
    );
  }

  get proteinGrams(): number {
    return Macronutrients.macronutrientGrams(
      "proteins",
      this.proteinPercent,
      this.store.recommendedCaloriesKcal
    );
  }

  get fatGrams(): number {
    return Macronutrients.macronutrientGrams(
      "fats",
      this.fatPercent,
      this.store.recommendedCaloriesKcal
    );
  }

  get carbohydrateCalories(): number {
    return this.store.recommendedCaloriesKcal * this.carbohydratePercent;
  }

  get proteinCalories(): number {
    return this.store.recommendedCaloriesKcal * this.proteinPercent;
  }

  get fatCalories(): number {
    return this.store.recommendedCaloriesKcal * this.fatPercent;
  }

  constructor(store: MealPlanFormStore) {
    makeAutoObservable(this);
    this.store = store;
  }

  set(name: "carbohydrates" | "proteins" | "fats", value: string): void {
    this[name] = value;
  }

  toAttributes() {
    return {
      macronutrientsCarbohydratePercent: toNum(this.carbohydrates),
      macronutrientsProteinPercent: toNum(this.proteins),
      macronutrientsFatPercent: toNum(this.fats),
    };
  }

  static macronutrientGrams(
    macro: "carbohydrates" | "proteins" | "fats",
    valuePercent: number,
    calories: number
  ): number {
    const caloriesPerMacroGram = {
      carbohydrates: CALORIES_PER_CARBOHYDRATE_GRAM,
      proteins: CALORIES_PER_PROTEIN_GRAM,
      fats: CALORIES_PER_FAT_GRAM,
    }[macro];
    const multiplier = valuePercent / caloriesPerMacroGram;
    return calories * multiplier;
  }
}
